import React, { useState } from "react";
import { WordTemplateInfo } from "./models";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import CultureLabel from "components/Culture/CultureLabel";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { WordTemplateFilePanel } from "components/FileUpload/SingleFilePanel";
import { LinkedBookkeepingsWordTemplatePanel } from "components/linkedBookkeepings/LinkedBookkeepingsWordTemplate";
import { findEnumObject } from "utilities/enumutils";

interface WordTemplateDetailSectionProps {
  wordTemplate: server.dto.WordTemplate
  displayVisibleToExternal: boolean
  displayReadyForESign: boolean
  displayVisibleBookkeeping: boolean
}

export const WordTemplateDetailSection = (props: WordTemplateDetailSectionProps) => {
  const [wordTemplate, setWordTemplate] = useState<WordTemplateInfo>({
    title: props.wordTemplate.title,
    visible: props.wordTemplate.visible,
    lcid: props.wordTemplate.lcid,
    eSignReady: props.wordTemplate.eSignReady,
    visibleToExternal: props.wordTemplate.visibleToExternal,
  });

  const culture = findEnumObject(wordTemplate.lcid, serverReference.cultures);

  const onValueChanged = (key: string, value: any) => {
    if (key === "title") {
      setWordTemplate({ ...wordTemplate, title: value });
    } else if (key === "visible") {
      setWordTemplate({ ...wordTemplate, visible: value });
    } else if (key === "lcid") {
      setWordTemplate({ ...wordTemplate, lcid: value });
    } else if (key === "eSignReady") {
      setWordTemplate({ ...wordTemplate, eSignReady: value });
    } else if (key === "visibleToExternal") {
      setWordTemplate({ ...wordTemplate, visibleToExternal: value });
    }
  }

  const getUpdateWordTemplateInstruction = (wt: WordTemplateInfo): server.dto.UpdateWordTemplateInstruction => ({
    wordTemplateForDetailViewId: props.wordTemplate.wordTemplateId,
    eSignReady: wt.eSignReady,
    culture: wt.lcid,
    title: wt.title,
    visible: wt.visible,
    visibleToExternal: wt.visibleToExternal,
  });

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "wordTemplate",
      title: RESX.WordTemplate.resxWordTemplate,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.Title,
        render: () => <div>{wordTemplate.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.wordTemplate.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: wordTemplate.title,
          submitChange: (title: string) => window.CoreApi.WordTemplate.Update({
            ...getUpdateWordTemplateInstruction(wordTemplate),
            title,
          }),
        },
      }, {
        key: "visible",
        label: RESX.GeneralLabels.Visible,
        render: () => <YesNoText value={wordTemplate.visible} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: wordTemplate.visible,
          submitChange: (visible: boolean) => window.CoreApi.WordTemplate.Update({
            ...getUpdateWordTemplateInstruction(wordTemplate),
            visible
          }),
        },
      }, {
        key: "lcid",
        label: RESX.Language.resxLanguage,
        render: () => <CultureLabel culture={culture} />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.LCID>) => <CulturesTemplateDropdown
            value={value}
            onChange={(c) => {
              onChange(c, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: wordTemplate.lcid,
          submitChange: (lcid: server.dto.LCID) => window.CoreApi.WordTemplate.Update({
            ...getUpdateWordTemplateInstruction(wordTemplate),
            culture: lcid,
          })
        },
      }, {
        key: "eSignReady",
        label: RESX.ESign.eSignReady,
        hidden: !props.displayReadyForESign,
        render: () => <YesNoText value={wordTemplate.eSignReady} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: wordTemplate.eSignReady,
          submitChange: (eSignReady: boolean) => window.CoreApi.WordTemplate.Update({
            ...getUpdateWordTemplateInstruction(wordTemplate),
            eSignReady
          }),
        },
      }, {
        key: "visibleToExternal",
        label: RESX.GeneralLabels.VisibleToExternalUsers,
        hidden: !props.displayVisibleToExternal,
        render: () => <YesNoText value={wordTemplate.visibleToExternal} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: wordTemplate.visibleToExternal,
          submitChange: (visibleToExternal: boolean) => window.CoreApi.WordTemplate.Update({
            ...getUpdateWordTemplateInstruction(wordTemplate),
            visibleToExternal
          }),
        },
      }]
    }, {
      key: "file",
      type: "custom",
      render: () => <WordTemplateFilePanel
        key="files-panel"
        title={wordTemplate.title}
        wordTemplateId={props.wordTemplate.wordTemplateId}
        onUploadComplete={() => { }}
      />
    }, {
      key: "bookkeepings",
      type: "custom",
      hidden: !props.displayVisibleBookkeeping || !wordTemplate.visible,
      render: () => <LinkedBookkeepingsWordTemplatePanel
        key="bookkeepings"
        wordTemplateId={props.wordTemplate.wordTemplateId}
      />
    }]}
  />
}